/**
 * PatientAddressForm – Form for create/edit patient address (訪問住所)
 * Includes 住所補助 modal, 特別地域加算 modal, 算定対象 confirm, 地図 link.
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import type { InputRef } from 'antd';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import {
  AppButton,
  AppCard,
  AppCheckbox,
  AppForm,
  AppTextField,
  AppFormLabel,
} from '@/components/atoms';
import { PostalCodeFields } from '@/components/molecules';
import { AddressSearchModal } from '@/components/organisms';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useAddressLookup } from '@/hooks/useAddressLookup';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import {
  patientAddressFormSchema,
  type PatientAddressFormData,
} from '@/schemas/patientAddressSchema';
import {
  ADDRESS3_CALC_TARGET_CONFIRM_HINT,
  ADDRESS3_CALC_TARGET_CONFIRM_QUESTION,
  FACILITY_RESIDENT_UNCHECKED_HINT,
  FACILITY_RESIDENT_UNCHECKED_QUESTION,
  SPECIAL_AREA_ADDITION_WARNING,
} from '@pages/main/patient/constants/validationMessages';
import { buildPatientAddressFormInitialValues } from './patientAddressFormInitialValues';

const toFullWidth = (str: string) =>
  str
    .replace(/[\x21-\x7E]/g, c => String.fromCharCode(c.charCodeAt(0) + 0xfee0))
    .replace(/ /g, '\u3000');

const GOOGLE_MAPS_BASE = 'https://www.google.co.jp/maps?q=';

interface PatientAddressFormProps {
  formId: string;
  defaultValues?: Partial<PatientAddressFormData>;
  onSubmit: (data: PatientAddressFormData) => void;
}

const PatientAddressForm: React.FC<PatientAddressFormProps> = ({
  formId,
  defaultValues,
  onSubmit,
}) => {
  const toast = useAppToast();
  const { confirm } = useAppModal();
  const [addressSearchOpen, setAddressSearchOpen] = useState(false);
  const address2InputRef = useRef<InputRef | null>(null);

  const initialValues = useMemo(
    () => buildPatientAddressFormInitialValues(defaultValues),
    [defaultValues]
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
  } = useTrimmedForm<PatientAddressFormData>({
    resolver: zodResolver(patientAddressFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: initialValues,
  });
  const currentValues = watch();
  const { clearFormChange } = useFormChangeGuard(currentValues, initialValues);

  const handleAddressSelect = useCallback(
    (result: { postal_code1: string; postal_code2: string; address: string }) => {
      setValue('postal_code1', result.postal_code1);
      setValue('postal_code2', result.postal_code2);
      setValue('address1', result.address);
      setAddressSearchOpen(false);
    },
    [setValue]
  );

  const { search: handleAddressSearchClick, isPending: isAddressSearching } = useAddressLookup({
    getValues: () => ({
      pc1: watch('postal_code1') || '',
      pc2: watch('postal_code2') || '',
      address: watch('address1') || '',
    }),
    applyResult: ({ pc1, pc2, address }) => {
      setValue('postal_code1', pc1, { shouldDirty: true, shouldValidate: true });
      setValue('postal_code2', pc2, { shouldDirty: true, shouldValidate: true });
      setValue('address1', address, { shouldDirty: true, shouldValidate: true });
    },
    openModal: () => setAddressSearchOpen(true),
    onAfterApply: () => {
      address2InputRef.current?.focus();
    },
  });

  const handleMapClick = useCallback(() => {
    const address1 = watch('address1');
    if (!address1 || address1.trim() === '') {
      return;
    }
    const address =
      address1 + (watch('address2') || '') + (watch('address3') || '') + (watch('name') || '');
    window.open(GOOGLE_MAPS_BASE + encodeURIComponent(address), '_blank');
  }, [watch]);

  const handleAddress3CheckChange = useCallback(
    (checked: boolean, onChange: (v: boolean) => void) => {
      if (checked) {
        confirm({
          title: (
            <>
              {ADDRESS3_CALC_TARGET_CONFIRM_QUESTION}
              <br />
              {ADDRESS3_CALC_TARGET_CONFIRM_HINT}
            </>
          ),
          cancelText: 'キャンセル',
          onOk: () => onChange(true),
          onCancel: () => onChange(false),
        });
      } else {
        onChange(false);
      }
    },
    [confirm]
  );

  const handleSpecialAreaChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        confirm({
          title: SPECIAL_AREA_ADDITION_WARNING,
          okText: 'はい',
          cancelText: 'いいえ',
          onOk: () => setValue('is_special_area_addition', true),
          onCancel: () => setValue('is_special_area_addition', false),
        });
      } else {
        setValue('is_special_area_addition', false);
      }
    },
    [confirm, setValue]
  );

  /**
   * 令和8年法改正 No 11 — ask before saving a 訪問住所 with 施設入居 unticked.
   *
   * Not a validation error: a real 患家 address saves with it off. But the tick is what
   * groups everyone at that address into one head count, and getting it wrong bills the
   * whole building at 訪問施術料① — an under-charge nobody notices until the 返戻.
   *
   * Cancelling leaves the form untouched so the user can tick and save again.
   */
  const submitWithFacilityConfirm = useCallback(
    (data: PatientAddressFormData) =>
      new Promise<void>(resolve => {
        if (data.is_facility_resident) {
          resolve();

          return;
        }
        confirm({
          title: (
            // HIT-11383 — antd bolds .ant-modal-confirm-title, but this slot carries two
            // sentences of body copy rather than a heading, and QC filed the weight as a
            // UI defect. Unbolded here rather than by moving the copy to `content`, which
            // would also drop it to 14px and shift the icon.
            <span className="font-normal">
              {FACILITY_RESIDENT_UNCHECKED_HINT}
              <br />
              {FACILITY_RESIDENT_UNCHECKED_QUESTION}
            </span>
          ),
          cancelText: 'キャンセル',
          onOk: () => resolve(),
          onCancel: () => {
            /* keep the form open so the box can be ticked */
          },
        });
      }),
    [confirm]
  );

  return (
    <>
      <AppCard
        className="bg-[#fdf5e6]"
        style={{
          WebkitBoxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
          boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
        }}
      >
        <form
          id={formId}
          onSubmit={handleSubmit(
            async data => {
              await submitWithFacilityConfirm(data);
              await onSubmit(data);
              clearFormChange();
            },
            () => {
              toast.error('更新ができませんでした。');
            }
          )}
        >
          <p className="mb-4 text-sm text-amber-700">
            入居施設等、保険証住所と異なる場所を登録してください。（保険証住所と同じ住所は登録しないでください）
          </p>

          <div className="grid grid-cols-[240px_1fr] gap-x-6 gap-y-4 items-start">
            {/* 郵便番号 */}
            <AppFormLabel label="郵便番号" required />
            <div>
              <Controller
                name="postal_code1"
                control={control}
                render={({ field: pc1, fieldState: { error: e1 } }) => (
                  <Controller
                    name="postal_code2"
                    control={control}
                    render={({ field: pc2, fieldState: { error: e2 } }) => (
                      <PostalCodeFields
                        postalCode1={pc1.value || ''}
                        postalCode2={pc2.value || ''}
                        onPostalCode1Change={pc1.onChange}
                        onPostalCode2Change={pc2.onChange}
                        onPostalCode2Autofill={next =>
                          setValue('postal_code2', next, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        errorPostalCode1={e1?.message}
                        errorPostalCode2={e2?.message}
                        onSearch={handleAddressSearchClick}
                        searchLoading={isAddressSearching || addressSearchOpen}
                      />
                    )}
                  />
                )}
              />
            </div>

            {/* 住所 */}
            <AppFormLabel label="住所" required />
            <div className="flex flex-wrap items-center gap-2">
              <Controller
                name="address1"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    className="mb-0 flex-1 min-w-[200px]"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...field}
                      maxLength={255}
                      status={fieldState.invalid ? 'error' : undefined}
                      onChange={e => field.onChange(toFullWidth(e.target.value))}
                    />
                  </AppForm.Item>
                )}
              />
              <AppButton type="primary" htmlType="button" onClick={handleMapClick}>
                地図
              </AppButton>
            </div>

            {/* 住所2 */}
            <div />
            <Controller
              name="address2"
              control={control}
              render={({ field, fieldState }) => {
                const { ref: rhfRef, ...fieldProps } = field;
                return (
                  <AppForm.Item
                    className="mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...fieldProps}
                      ref={instance => {
                        address2InputRef.current = instance;
                        rhfRef(instance);
                      }}
                      className="w-full"
                      status={fieldState.invalid ? 'error' : undefined}
                      onChange={e => field.onChange(toFullWidth(e.target.value))}
                    />
                  </AppForm.Item>
                );
              }}
            />

            {/* 建物・部署 */}
            <AppFormLabel label="建物・部署" />
            <Controller
              name="address3"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    className="w-full"
                    status={fieldState.invalid ? 'error' : undefined}
                    onChange={e => field.onChange(toFullWidth(e.target.value))}
                  />
                </AppForm.Item>
              )}
            />

            {/* 名称 */}
            <AppFormLabel label="名称" />
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    className="w-full"
                    status={fieldState.invalid ? 'error' : undefined}
                    onChange={e => field.onChange(toFullWidth(e.target.value))}
                  />
                </AppForm.Item>
              )}
            />

            {/* 常に選択表示 + 特別地域加算 + 算定対象 */}
            <div />
            <div className="flex flex-wrap items-center gap-6">
              <Controller
                name="is_default"
                control={control}
                render={({ field }) => (
                  <AppCheckbox
                    checked={field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  >
                    常に選択表示
                  </AppCheckbox>
                )}
              />
              <Controller
                name="is_facility_resident"
                control={control}
                render={({ field }) => (
                  <AppCheckbox
                    checked={field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  >
                    施設入居
                  </AppCheckbox>
                )}
              />
              <Controller
                name="is_special_area_addition"
                control={control}
                render={({ field }) => (
                  <AppCheckbox
                    checked={field.value}
                    onChange={e => handleSpecialAreaChange(e.target.checked)}
                  >
                    特別地域加算
                  </AppCheckbox>
                )}
              />
              <Controller
                name="address3_check"
                control={control}
                render={({ field }) => (
                  <AppCheckbox
                    checked={field.value}
                    onChange={e => handleAddress3CheckChange(e.target.checked, field.onChange)}
                  >
                    算定対象
                  </AppCheckbox>
                )}
              />
            </div>
          </div>
        </form>
      </AppCard>

      <AddressSearchModal
        isOpen={addressSearchOpen}
        onClose={() => setAddressSearchOpen(false)}
        onSelect={handleAddressSelect}
        initialPostalCode1={watch('postal_code1')}
        initialPostalCode2={watch('postal_code2')}
        initialAddress={
          !watch('postal_code1') && !watch('postal_code2') ? (watch('address1') ?? '') : ''
        }
        title="住所補助"
        focusAfterSelectRef={address2InputRef}
      />
    </>
  );
};

export default PatientAddressForm;
